// src/redux/driversSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import Configuration from "../configuration";

// Fetch all drivers
export const fetchDrivers = createAsyncThunk("drivers/fetchAll", async () => {
  const token = localStorage.getItem("x-access-token");
  const response = await fetch(Configuration.BACK_BASEURL + "driver", {
    method: "GET",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      /* "x-access-token": token, */
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || "Failed to fetch drivers");
  }

  const drivers = await response.json();
  return drivers;
});

// Fetch active drivers
export const fetchActiveDrivers = createAsyncThunk(
  "drivers/fetchActive",
  async () => {
    const token = localStorage.getItem("x-access-token");
    const response = await fetch(Configuration.BACK_BASEURL + "driver/active", {
      method: "GET",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        /* "x-access-token": token, */
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || "Failed to fetch active drivers");
    }

    const drivers = await response.json();
    return drivers;
  },
);

// Fetch driver by ID
export const driverGetById = createAsyncThunk("drivers/getById", async (id) => {
  const token = localStorage.getItem("x-access-token");
  const response = await fetch(Configuration.BACK_BASEURL + `driver/${id}`, {
    method: "GET",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      /* "x-access-token": token, */
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || "Failed to fetch driver");
  }

  const driver = await response.json();
  return driver;
});

// Fetch drivers by car ID
export const fetchDriversByCar = createAsyncThunk(
  "drivers/fetchByCar",
  async (carId) => {
    const token = localStorage.getItem("x-access-token");
    const response = await fetch(
      Configuration.BACK_BASEURL + `driver/car/${carId}`,
      {
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          /* "x-access-token": token, */
        },
      },
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || "Failed to fetch drivers by car");
    }

    const drivers = await response.json();
    return drivers;
  },
);

// Create new driver
export const driverAdded = createAsyncThunk(
  "drivers/create",
  async (driverData, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("x-access-token");
      const response = await fetch(Configuration.BACK_BASEURL + "driver", {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          /* "x-access-token": token, */
        },
        body: JSON.stringify(driverData),
      });

      const data = await response.json();

      if (!response.ok) {
        // Return the entire error object from backend
        return rejectWithValue(data);
      }

      return data;
    } catch (error) {
      return rejectWithValue({ message: error.message || "Network error" });
    }
  },
);

// Update driver
export const driverUpdated = createAsyncThunk(
  "drivers/update",
  async ({ id, ...driverData }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("x-access-token");
      const response = await fetch(
        Configuration.BACK_BASEURL + `driver/${id}`,
        {
          method: "PATCH",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            /* "x-access-token": token, */
          },
          body: JSON.stringify(driverData),
        },
      );

      const data = await response.json();

      if (!response.ok) {
        // Return the entire error object from backend
        return rejectWithValue(data);
      }

      return data;
    } catch (error) {
      return rejectWithValue({ message: error.message || "Network error" });
    }
  },
);

// Delete driver
export const driverDeleted = createAsyncThunk(
  "drivers/delete",
  async (id, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("x-access-token");
      const response = await fetch(
        Configuration.BACK_BASEURL + `driver/${id}`,
        {
          method: "DELETE",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            /* "x-access-token": token, */
          },
        },
      );

      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(data);
      }

      return id;
    } catch (error) {
      return rejectWithValue({ message: error.message || "Network error" });
    }
  },
);

// Toggle driver active status
export const toggleDriverActive = createAsyncThunk(
  "drivers/toggleActive",
  async (id, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("x-access-token");
      const response = await fetch(
        Configuration.BACK_BASEURL + `driver/${id}/toggle-active`,
        {
          method: "PATCH",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            /* "x-access-token": token, */
          },
        },
      );

      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(data);
      }

      return data;
    } catch (error) {
      return rejectWithValue({ message: error.message || "Network error" });
    }
  },
);

const driversSlice = createSlice({
  name: "drivers",
  initialState: {
    entities: [],
    loading: false,
    error: null,
    success: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all drivers
      .addCase(fetchDrivers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDrivers.fulfilled, (state, action) => {
        state.loading = false;
        state.entities = action.payload;
      })
      .addCase(fetchDrivers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      // Fetch active drivers
      .addCase(fetchActiveDrivers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchActiveDrivers.fulfilled, (state, action) => {
        state.loading = false;
        state.entities = action.payload;
      })
      .addCase(fetchActiveDrivers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      // Fetch drivers by car
      .addCase(fetchDriversByCar.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDriversByCar.fulfilled, (state, action) => {
        state.loading = false;
        state.entities = action.payload;
      })
      .addCase(fetchDriversByCar.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      // Add driver
      .addCase(driverAdded.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(driverAdded.fulfilled, (state, action) => {
        state.loading = false;
        state.entities.push(action.payload);
        state.success = "Driver created successfully";
      })
      .addCase(driverAdded.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || action.error.message;
      })

      // Update driver
      .addCase(driverUpdated.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(driverUpdated.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.entities.findIndex(
          (driver) => driver.id === action.payload.id,
        );
        if (index !== -1) {
          state.entities[index] = action.payload;
        }
        state.success = "Driver updated successfully";
      })
      .addCase(driverUpdated.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || action.error.message;
      })

      // Delete driver
      .addCase(driverDeleted.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(driverDeleted.fulfilled, (state, action) => {
        state.loading = false;
        state.entities = state.entities.filter(
          (driver) => driver.id !== action.payload,
        );
        state.success = "Driver deleted successfully";
      })
      .addCase(driverDeleted.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || action.error.message;
      })

      // Toggle driver active
      .addCase(toggleDriverActive.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(toggleDriverActive.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.entities.findIndex(
          (driver) => driver.id === action.payload.id,
        );
        if (index !== -1) {
          state.entities[index] = action.payload;
        }
        state.success = "Driver status toggled successfully";
      })
      .addCase(toggleDriverActive.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || action.error.message;
      });
  },
});

export const { clearError, clearSuccess } = driversSlice.actions;
export default driversSlice.reducer;
